import { useEffect, useRef } from "react";

import type { FlowAnswers } from "@/shared/constants/flowTypes";

interface UseReviewRedirectOptions {
  items: unknown[];
  isLoading: boolean;
  isFetching: boolean;
  answers: FlowAnswers;
  setAnswer: (key: string, value: string) => void;
  goToScreen: (id: string, answers: FlowAnswers) => void;
  reviewKey: string;
  addKey: string;
  fallbackScreen: string;
  fallbackAnswers: FlowAnswers;
}

export function useReviewRedirect({
  items,
  isLoading,
  isFetching,
  answers,
  setAnswer,
  goToScreen,
  reviewKey,
  addKey,
  fallbackScreen,
  fallbackAnswers,
}: UseReviewRedirectOptions) {
  const hasRedirected = useRef(false);
  const itemsEverFound = useRef(false);

  useEffect(() => {
    if (isLoading || isFetching) return;

    if (items.length > 0) {
      hasRedirected.current = false;
      itemsEverFound.current = true;
      if (answers[reviewKey] !== "found") {
        setAnswer(reviewKey, "found");
      }
      if (answers[addKey] === "adding") {
        setAnswer(addKey, "done");
      }
    } else if (!hasRedirected.current && !itemsEverFound.current && answers[reviewKey] !== "none") {
      hasRedirected.current = true;
      goToScreen(fallbackScreen, fallbackAnswers);
    }
  }, [
    isLoading,
    isFetching,
    items.length,
    answers,
    setAnswer,
    goToScreen,
    reviewKey,
    addKey,
    fallbackScreen,
    fallbackAnswers,
  ]);

  const alreadyRedirected = answers[reviewKey] === "none";

  return {
    itemsEverFound: itemsEverFound.current,
    alreadyRedirected,
  };
}
